import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowLeftIcon, InfoIcon, TriangleAlertIcon } from "lucide-react";

import { CopyButton } from "~/components/copy-button";
import { DetailField, FieldGrid } from "~/components/detail-field";
import { PageHeader } from "~/components/page-header";
import { StatusBadge } from "~/components/status-badge";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { errorMessage, isNotFound } from "~/lib/api";
import { EM_DASH, formatDate, formatDateTime, formatIDR, formatIDROptional } from "~/lib/format";
import { useSettlement, type SettlementDetail } from "~/lib/queries";

export const Route = createFileRoute("/_authed/settlements/$settlementId")({
  component: SettlementDetailPage,
});

/**
 * One settlement batch and the orders it covers.
 *
 * Read-only, like the list. The screen answers "what was in this payout" and
 * stops there — see NoMerchantSplit for the one thing it deliberately does not
 * do, and why that is a decision rather than an omission.
 */
function SettlementDetailPage() {
  const { settlementId } = Route.useParams();
  const { data, error, isPending, refetch } = useSettlement(settlementId);

  if (isPending) return <DetailSkeleton />;

  if (isNotFound(error)) {
    return <EmptyState body="Nothing in settlements has this id." />;
  }

  if (error || !data) {
    return <EmptyState body={errorMessage(error)} onRetry={() => void refetch()} />;
  }

  return (
    <div className="space-y-5">
      <div>
        <Button variant="ghost" size="sm" render={<Link to="/settlements" />}>
          <ArrowLeftIcon className="size-3.5" />
          All settlements
        </Button>
      </div>

      <PageHeader
        title={
          <span className="flex items-center gap-2">
            <span className="font-mono text-xl">
              {data.psp_settlement_id ?? data.settlement_id}
            </span>
            <CopyButton
              value={data.psp_settlement_id ?? data.settlement_id}
              label="settlement id"
            />
          </span>
        }
        description={
          <span className="flex flex-wrap items-center gap-2">
            {data.status ? <StatusBadge status={data.status} /> : null}
            <span className="text-base font-medium text-foreground tabular-nums">
              {formatIDR(data.settlement_amount)}
            </span>
            {data.settlement_date ? (
              <span className="text-muted-foreground">
                settled {formatDate(data.settlement_date)}
              </span>
            ) : null}
          </span>
        }
      />

      {data.duplicate_psp_id ? <DuplicateWarning detail={data} /> : null}

      <Amounts detail={data} />
      <Orders detail={data} />
      <NoMerchantSplit />
    </div>
  );
}

/**
 * The money, with absent values kept absent.
 *
 * Only settlement_amount is NOT NULL in the live table. original_amount and
 * settlement_fee are nullable, and formatIDROptional renders nil as an em dash
 * rather than "Rp 0" — the distinction between "no fee was charged" and "no fee
 * was recorded" is one a finance person will act on, and collapsing it is the
 * specific failure this screen was asked to avoid.
 */
function Amounts({ detail }: { detail: SettlementDetail }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Amounts</CardTitle>
        <CardDescription>
          Blank means the value was never recorded, not that it was zero.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <FieldGrid>
          <DetailField label="Original amount">
            <span className="tabular-nums">{formatIDROptional(detail.original_amount)}</span>
          </DetailField>
          <DetailField label="Settled amount">
            <span className="tabular-nums">{formatIDR(detail.settlement_amount)}</span>
          </DetailField>
          <DetailField label="Fee">
            <span className="tabular-nums">{formatIDROptional(detail.settlement_fee)}</span>
          </DetailField>
          <DetailField label="Currency">{detail.currency ?? EM_DASH}</DetailField>
          <DetailField label="Settlement ID" copy={detail.settlement_id} mono>
            {detail.settlement_id}
          </DetailField>
          <DetailField label="Recorded">{formatDateTime(detail.created_at)}</DetailField>
        </FieldGrid>
      </CardContent>
    </Card>
  );
}

/** The orders in the batch. */
function Orders({ detail }: { detail: SettlementDetail }) {
  return (
    <Card className="overflow-hidden">
      <CardHeader>
        <CardTitle className="text-base">
          Orders{" "}
          <span className="font-normal text-muted-foreground tabular-nums">
            {detail.transaction_count}
          </span>
        </CardTitle>
        <CardDescription>The transactions this settlement covers.</CardDescription>
      </CardHeader>

      <CardContent className="px-0 pb-0">
        {detail.orders.length === 0 ? (
          <p className="px-6 pb-6 text-sm text-muted-foreground">
            No settlement_transactions rows reference this settlement.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-6">Order ID</TableHead>
                <TableHead>Storefront</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Method</TableHead>
                <TableHead className="pr-6 text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {detail.orders.map((order) => (
                <TableRow key={order.order_id}>
                  <TableCell className="pl-6">
                    {/* Linked only when the transaction still exists.
                        settlement_transactions.order_id is ON DELETE SET NULL,
                        so a junction row can outlive its order, and a link that
                        404s reads as a missing order rather than a deleted one. */}
                    {order.transaction_found ? (
                      <Link
                        to="/transactions/$orderId"
                        params={{ orderId: order.order_id }}
                        className="font-mono text-[0.8125rem] text-primary hover:underline"
                      >
                        {order.order_id}
                      </Link>
                    ) : (
                      <span
                        className="font-mono text-[0.8125rem] text-muted-foreground"
                        title="This order is referenced by the settlement but no longer exists in transactions"
                      >
                        {order.order_id}
                      </span>
                    )}
                  </TableCell>

                  <TableCell className="max-w-[14rem] truncate">
                    {order.storefront_id && order.storefront_name ? (
                      <Link
                        to="/storefronts/$storefrontId"
                        params={{ storefrontId: order.storefront_id }}
                        className="text-primary hover:underline"
                      >
                        {order.storefront_name}
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">{EM_DASH}</span>
                    )}
                  </TableCell>

                  <TableCell>
                    {order.payment_status ? (
                      <StatusBadge status={order.payment_status} />
                    ) : (
                      <span className="text-muted-foreground">{EM_DASH}</span>
                    )}
                  </TableCell>

                  <TableCell className="text-muted-foreground">
                    {order.payment_method ?? EM_DASH}
                  </TableCell>

                  <TableCell className="pr-6 text-right tabular-nums">
                    {formatIDROptional(order.amount)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Why there is no "settled per merchant" total on this page.
 *
 * The join that would produce one is right there and is genuinely tempting —
 * settlement_transactions → transactions → storefronts, and every column is
 * already on screen. Saying plainly that it was left out on purpose is what
 * stops it being added next quarter by someone who assumes it was forgotten.
 */
function NoMerchantSplit() {
  return (
    <Card className="border-dashed bg-transparent shadow-none">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm">
          <InfoIcon className="size-3.5 text-muted-foreground" />
          No per-merchant split, deliberately
        </CardTitle>
        <CardDescription className="text-xs">
          Settlements are DurianPay paying Kugie — the table has no storefront_id, and the
          per-merchant share does not exist at this layer. The orders above name the merchant
          each belongs to, but nothing here totals them by merchant. That report is the ledger
          project's, and a second version computed here would be a second number that
          disagrees with it. The ledger's own interim report carries an explicit warning that
          even it must not be treated as a ledger or invoiced against.
        </CardDescription>
      </CardHeader>
    </Card>
  );
}

/**
 * A psp_settlement_id that is on more than one row.
 *
 * internal/models/settlement.go tags this column `uniqueIndex`; the live table
 * has no such constraint (documented drift in migrations/000), so duplicates are
 * insertable and this is not hypothetical. Worth saying out loud on the page
 * rather than silently showing one of several.
 */
function DuplicateWarning({ detail }: { detail: SettlementDetail }) {
  return (
    <Card className="border-warning/30 bg-warning/5">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base text-warning">
          <TriangleAlertIcon className="size-4" />
          This PSP settlement ID is on more than one settlement
        </CardTitle>
        <CardDescription>
          <code className="font-mono text-xs">{detail.psp_settlement_id}</code> appears on
          several rows. The application treats this column as unique and the database does not
          enforce it, so the duplicates are real data rather than a display error. This page
          is addressed by settlement_id, so it is showing exactly one of them — but any figure
          reconciled by PSP settlement ID will double-count.
        </CardDescription>
      </CardHeader>
    </Card>
  );
}

function DetailSkeleton() {
  return (
    <div className="space-y-5">
      <Skeleton className="h-8 w-72" />
      <Skeleton className="h-44 w-full" />
      <Skeleton className="h-64 w-full" />
    </div>
  );
}

function EmptyState({ body, onRetry }: { body: React.ReactNode; onRetry?: () => void }) {
  return (
    <div className="mx-auto max-w-lg">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Couldn't open this settlement</CardTitle>
          <CardDescription>{body}</CardDescription>
        </CardHeader>
        <CardContent className="flex gap-2">
          {onRetry ? (
            <Button variant="outline" size="sm" onClick={onRetry}>
              Try again
            </Button>
          ) : null}
          <Button variant="ghost" size="sm" render={<Link to="/settlements" />}>
            Back to settlements
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
